import { UseQueryResult } from '@tanstack/react-query'
import stringify from 'fast-safe-stringify'
import { useEffect } from 'react'

import { dispatchErrorToast } from '~/store/slices/toasterSlice'

import { DEFAULT_ERROR_MESSAGE } from './errorMessages'

type UseQueryErrorResult = Pick<UseQueryResult, 'isError' | 'error'>

type UseQueriesCombineErrorResult = {
    isError: boolean
    errors: string[]
}

type SentryLog<T> = { key: string; queryResult: T; toastNotification?: { showToast?: boolean; toastMessage?: string } }

/**
 * Logs error(s) to Sentry for a given query result.
 * It supports useQuery and `combined` results from useQueries (provided that `combined` returns `isError` and `errors`).
 * It also optionally displays a toast notification.
 *
 * @param SentryLog - An object containing the following properties:
 * @param key - A unique identifier for the query, used for grouping Sentry logs.
 * @param queryResult - The result of the query, containing its state and error information.
 * @param showToast - (Optional) If `true`, displays a toast notification.
 * @param toastMessage - (Optional) Error message to be displayed in the toast. Defaults to a generic error message.
 */
export function useSentryLogging<T extends UseQueryErrorResult | UseQueriesCombineErrorResult>({ key, queryResult, toastNotification = {} }: SentryLog<T>) {
    const errorMessage =
        'errors' in queryResult ? `Number of errors: ${queryResult.errors.length}: ${stringify(queryResult.errors)} ` : queryResult.error?.message

    useEffect(() => {
        if (queryResult.isError) {
            console.error(key, errorMessage)
        }
    }, [queryResult.isError, key, errorMessage])

    const { showToast = false, toastMessage = DEFAULT_ERROR_MESSAGE } = toastNotification

    // Separate useEffect for toasts as potential UI changes shouldn't affect the Sentry logging
    useEffect(() => {
        if (queryResult.isError) {
            showToast && dispatchErrorToast({ message: toastMessage })
        }
    }, [queryResult.isError, showToast, toastMessage])
}
